import { CheckCircle2, Circle, ExternalLink, Loader2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDashboardPrerequisites } from '../hooks/use-dashboard-prerequisites';

interface InitialSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Define the steps (same as checklist for consistency)
const setupSteps = [
  {
    id: 'logo',
    label: 'Upload Company Logo',
    isComplete: (status: ReturnType<typeof useDashboardPrerequisites>) => !!status.logoUrl,
    link: '/dashboard/logo',
  },
  {
    id: 'mappings',
    label: 'Configure Field Mappings',
    isComplete: (status: ReturnType<typeof useDashboardPrerequisites>) => status.hasMappings,
    link: '/dashboard/field-mapping',
  },
  {
    id: 'settings',
    label: 'Visit Settings Page',
    isComplete: (status: ReturnType<typeof useDashboardPrerequisites>) => status.hasViewedSettings,
    link: '/dashboard/settings',
  },
];

/**
 * A modal shown once on initial dashboard load, reflecting current setup status.
 */
export const InitialSetupModal = ({ isOpen, onClose }: InitialSetupModalProps) => {
  const status = useDashboardPrerequisites();
  const { dashboardInitialized } = status;
  const completedCount = setupSteps.filter((step) => step.isComplete(status)).length;
  const totalSteps = setupSteps.length;
  const progress = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 pt-10 max-w-sm w-full relative shadow-xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="text-center">
          <h2 className="text-lg font-semibold mb-2 text-gray-800">Welcome! Let's Get Started</h2>
          <p className="text-xs text-gray-500 mb-3">
            {dashboardInitialized
              ? `${completedCount} of ${totalSteps} setup steps complete`
              : 'Checking setup status...'}
          </p>
          {/* Progress Bar */}
          {dashboardInitialized && (
            <div className="w-full bg-gray-200 rounded-full h-1.5 mb-5">
              <div
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          )}
          {!dashboardInitialized && (
            <div className="w-full bg-gray-200 rounded-full h-1.5 mb-5 relative overflow-hidden">
              <div className="absolute inset-0 bg-blue-300 opacity-50"></div>
            </div>
          )}
          <p className="text-sm text-gray-600 mb-4">Please complete the following steps:</p>

          {/* Dynamic List of Steps */}
          <ul className="divide-y divide-gray-100 p-2 mb-5 border rounded-md text-left">
            {!dashboardInitialized && (
              <li className="py-2 px-1 flex items-center text-gray-500 text-sm">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...
              </li>
            )}
            {dashboardInitialized &&
              setupSteps.map((step) => {
                const isComplete = step.isComplete(status);
                return (
                  <li key={step.id} className="py-1 px-1">
                    {isComplete ? (
                      // Render a div for completed steps
                      <div className="flex items-center justify-between text-sm rounded-md p-1.5 text-gray-400 cursor-default">
                        <div className="flex items-center">
                          <CheckCircle2 className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" />
                          <span className="line-through">{step.label}</span>
                        </div>
                      </div>
                    ) : (
                      // Render a Link for incomplete steps
                      <Link
                        to={step.link}
                        className="flex items-center justify-between text-sm rounded-md p-1.5 group text-gray-700 hover:bg-gray-100"
                        onClick={onClose} // Close modal when link is clicked
                      >
                        <div className="flex items-center">
                          <Circle className="h-4 w-4 mr-2 text-gray-300 flex-shrink-0" />
                          <span>{step.label}</span>
                        </div>
                        <ExternalLink className="h-3.5 w-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    )}
                  </li>
                );
              })}
          </ul>

          {/* Action button */}
          <button
            onClick={onClose}
            className="w-full bg-blue-600 text-white rounded-md py-2.5 px-4 text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Continue to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};
